import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge, Button } from '@/components/atoms';
import {
  FlowTimeline,
  NodePanel,
  PainelDeErro,
  WireseoEsteiras,
  WireseoFontes,
  WireseoParams,
  WireseoRegistry,
} from '@/components/engineering';
import { articlesApi } from '@/services/articles';
import {
  aceitaPacoteDeMateria,
  engineeringService,
  type FlowStage,
  type RunStageOut,
} from '@/services/engineering';
import { usePlatformAccess } from '@/services/platform';
import { useAuth } from '@/store/useAuth';
import { destinoPadrao } from '@/config/escopo';

import styles from './Engineering.module.css';

type Aba = 'fluxos' | 'parametros' | 'registros' | 'fontes' | 'esteiras';

const ABAS: { key: Aba; rotulo: string }[] = [
  { key: 'fluxos', rotulo: 'Fluxos' },
  { key: 'parametros', rotulo: 'Parâmetros' },
  { key: 'registros', rotulo: 'Tipos e tons' },
  { key: 'fontes', rotulo: 'Fontes oficiais' },
  { key: 'esteiras', rotulo: 'Esteiras' },
];

/**
 * Engenharia de prompt — ADMIN-ONLY (a rota já é protegida; aqui só o papel
 * fecha a tela para quem não é admin).
 *
 * A vista é a LINHA DO TEMPO HORIZONTAL do plano WIRESEO §11.1: um fluxo por
 * vez, todos os nós na sequência real (código e IA), painel do nó embaixo. As
 * abas de configuração ficam nesta mesma rota — decisão registrada na entrega:
 * abrir rota nova exigiria mexer no router, que não é desta frente, e a
 * configuração só faz sentido ao lado do fluxo que ela governa.
 */
export function Engineering() {
  const role = useAuth((s) => s.role);
  const qc = useQueryClient();

  // O prompt é POR CLIENTE. A casa escolhe de quem está editando — sem isso o
  // backend recusa (antes caía num tenant implícito e mudava a saída dele).
  const { clients } = usePlatformAccess();
  const [client, setClient] = useState<string>('');
  useEffect(() => {
    // Nao escolher cliente sozinho: com varios, quem escolhe e a pessoa.
    if (!client) {
      const padrao = destinoPadrao(clients);
      if (padrao) setClient(padrao);
    }
  }, [clients, client]);

  const [aba, setAba] = useState<Aba>('fluxos');
  const [flowId, setFlowId] = useState<string | null>(null);
  const [stageId, setStageId] = useState<string | null>(null);
  const [insumo, setInsumo] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [outputs, setOutputs] = useState<Record<string, RunStageOut>>({});
  // Laudo do teste (dry_run) vem INLINE na resposta do run; laudo de execução
  // persistida vem por id e se lê no endpoint de edições. Guardo os dois em
  // separado para nunca exibir um dizendo que é o outro.
  const [laudoInline, setLaudoInline] = useState<unknown>(null);
  const [edicaoId, setEdicaoId] = useState<string | null>(null);

  const flowsQuery = useQuery({
    queryKey: ['eng-flows'],
    queryFn: engineeringService.listFlows,
    enabled: role === 'admin',
  });

  useEffect(() => {
    if (!flowId && flowsQuery.data && flowsQuery.data.length > 0) {
      setFlowId(flowsQuery.data[0].id);
    }
  }, [flowsQuery.data, flowId]);

  const flowQuery = useQuery({
    queryKey: ['eng-flow', flowId, client],
    queryFn: () => engineeringService.getFlow(flowId as string, client),
    enabled: Boolean(flowId) && Boolean(client),
  });

  // troca de fluxo OU de cliente = estado limpo. Sem o `client` aqui, a textarea
  // do cliente anterior ficaria na tela e um "salvar" gravaria no cliente novo.
  useEffect(() => {
    setEdits({});
    setOutputs({});
    setInsumo('');
    setStageId(null);
    setEdicaoId(null);
    setLaudoInline(null);
  }, [flowId, client]);

  // semeia o texto editável de cada estágio sem clobber de edição em curso
  useEffect(() => {
    if (!flowQuery.data) return;
    setEdits((prev) => {
      const next = { ...prev };
      for (const s of flowQuery.data.stages) {
        // nó de código chega com role_system null — não há texto para semear
        if (s.kind === 'ia' && next[s.id] === undefined) next[s.id] = s.role_system ?? '';
      }
      return next;
    });
    setStageId((atual) => atual ?? flowQuery.data.stages[0]?.id ?? null);
  }, [flowQuery.data]);

  const stages = useMemo(() => flowQuery.data?.stages ?? [], [flowQuery.data]);
  const stage: FlowStage | undefined = stages.find((s) => s.id === stageId);

  // Insumo do teste: quando o runner do fluxo aceita o pacote de uma matéria, a
  // lista é de rascunhos REAIS do WordPress — não há matéria de exemplo em
  // lugar nenhum desta tela.
  const usaPacote = flowQuery.data ? aceitaPacoteDeMateria(flowQuery.data) : false;
  const rascunhos = useQuery({
    queryKey: ['eng-rascunhos'],
    queryFn: () => articlesApi.list({ status: 'draft', per_page: 20 }),
    enabled: role === 'admin' && usaPacote,
    retry: false,
  });

  // Carrega o pacote da matéria escolhida e o joga no campo de insumo. O que
  // vai para o teste é o conteúdo real da matéria, lido do WordPress agora.
  const carregarPacote = useMutation({
    mutationFn: async (valor: string) => {
      const [id, site] = valor.split('|');
      const a = await articlesApi.get(Number(id), site || undefined);
      return JSON.stringify(
        { title: a.title, excerpt: a.excerpt ?? '', content_html: a.content_html },
        null,
        2,
      );
    },
    onSuccess: (json) => setInsumo(json),
  });

  const save = useMutation({
    mutationFn: (s: FlowStage) =>
      engineeringService.savePrompt(flowId as string, s.id, edits[s.id] ?? '', client),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['eng-flow', flowId, client] }),
  });

  // Restaurar padrão = grava vazio (backend remove o override → volta ao prompt
  // de fábrica), e repõe a textarea com o default localmente.
  const restore = useMutation({
    mutationFn: (s: FlowStage) => engineeringService.savePrompt(flowId as string, s.id, '', client),
    onSuccess: (_d, s) => {
      setEdits((p) => ({ ...p, [s.id]: s.default_system ?? '' }));
      qc.invalidateQueries({ queryKey: ['eng-flow', flowId, client] });
    },
  });

  const run = useMutation({
    mutationFn: (upto?: string) => engineeringService.run(flowId as string, insumo, client, upto),
    onSuccess: (r) => {
      setOutputs((prev) => {
        const next = { ...prev };
        for (const s of r.stages) next[s.id] = s;
        return next;
      });
      setLaudoInline(r.meta?.laudo ?? null);
      setEdicaoId(typeof r.meta?.edicao_id === 'string' ? r.meta.edicao_id : null);
    },
  });

  // Laudo persistido: só busca quando a execução devolveu um id de edição.
  const laudoQuery = useQuery({
    queryKey: ['eng-edicao', flowId, edicaoId, client],
    queryFn: () => engineeringService.getEdicao(flowId as string, edicaoId as string, client),
    enabled: Boolean(flowId) && Boolean(edicaoId) && Boolean(client),
    retry: false,
  });

  if (role !== 'admin') {
    return (
      <section className={styles.root}>
        <div className={styles.restricted}>
          <h1 className={styles.title}>Engenharia de Prompt</h1>
          <p className={styles.muted}>
            Área restrita ao <b>engenheiro de prompt</b> (papel admin). Peça acesso a um
            administrador.
          </p>
        </div>
      </section>
    );
  }

  const runInput = flowQuery.data?.run_input;
  const runSuportado = flowQuery.data?.run_suportado !== false;
  const podeRodar = Boolean(insumo.trim()) && !run.isPending && runSuportado;

  return (
    <section className={styles.root}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Engenharia de Prompt</h1>
          <p className={styles.subtitle}>
            Cada fluxo aparece inteiro, na ordem em que roda: nós de código e nós de IA. O Role dos
            nós de IA é editável e vale por cliente na produção. O teste roda sempre em simulação —
            esta tela não publica em lugar nenhum.
          </p>
        </div>
        <label className={styles.clientPicker}>
          <span>Cliente</span>
          <select value={client} onChange={(e) => setClient(e.target.value)}>
            {/* Sem esta option, o select MOSTRA o 1º cliente com o estado ainda
                vazio — e os fluxos não abrem sem nenhuma pista do porquê. */}
            {!client && <option value="">— escolha o cliente —</option>}
            {clients.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name ?? c.slug}
              </option>
            ))}
          </select>
        </label>
      </header>

      <nav className={styles.tabs}>
        {ABAS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${aba === t.key ? styles.tabActive : ''}`}
            onClick={() => setAba(t.key)}
          >
            {t.rotulo}
          </button>
        ))}
      </nav>

      {aba === 'fluxos' && (
        <>
          {/* seletor de fluxo — horizontal, o nome do fluxo manda na vista */}
          <div className={styles.flowChips}>
            {flowsQuery.isLoading && <span className={styles.muted}>Carregando fluxos…</span>}
            {(flowsQuery.data ?? []).map((f) => (
              <button
                key={f.id}
                type="button"
                className={`${styles.chip} ${f.id === flowId ? styles.chipAtivo : ''}`}
                onClick={() => setFlowId(f.id)}
                title={f.descricao}
              >
                <span className={styles.chipNome}>{f.nome}</span>
                <span className={styles.chipMeta}>{f.estagios} estágios</span>
              </button>
            ))}
          </div>

          {flowQuery.isLoading && <p className={styles.muted}>Carregando fluxo…</p>}
          <PainelDeErro
            endpoint={`GET /engineering/flows/${flowId ?? '{fluxo}'}`}
            o_que="A linha do fluxo e os prompts"
            erro={flowQuery.error}
          />

          {flowQuery.data && (
            <div className={styles.frame}>
              <header className={styles.frameHead}>
                <div className={styles.frameTitulo}>
                  <h2 className={styles.frameNome}>{flowQuery.data.nome}</h2>
                  <p className={styles.frameDesc}>{flowQuery.data.descricao}</p>
                </div>
                <div className={styles.runbar}>
                  <Badge tone="info" size="xs">
                    simulação — não publica
                  </Badge>
                  {usaPacote && (
                    <select
                      className={styles.insumo}
                      defaultValue=""
                      aria-label="Carregar o pacote de uma matéria em rascunho"
                      disabled={
                        rascunhos.isLoading || Boolean(rascunhos.error) || carregarPacote.isPending
                      }
                      onChange={(e) => {
                        if (e.target.value) carregarPacote.mutate(e.target.value);
                      }}
                    >
                      <option value="">
                        {rascunhos.error
                          ? 'rascunhos indisponíveis'
                          : carregarPacote.isPending
                            ? 'carregando matéria…'
                            : '— carregar rascunho real —'}
                      </option>
                      {(rascunhos.data?.items ?? []).map((a) => (
                        <option key={`${a.site}-${a.id}`} value={`${a.id}|${a.site}`}>
                          {a.title}
                        </option>
                      ))}
                    </select>
                  )}
                  {runInput?.kind === 'select' ? (
                    <select
                      className={styles.insumo}
                      value={insumo}
                      onChange={(e) => setInsumo(e.target.value)}
                      aria-label={runInput.label}
                    >
                      <option value="">— {runInput.label} —</option>
                      {(runInput.options ?? []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : usaPacote ? (
                    <textarea
                      className={styles.insumoArea}
                      value={insumo}
                      onChange={(e) => setInsumo(e.target.value)}
                      placeholder={runInput?.placeholder ?? 'Pacote em JSON ou texto da matéria'}
                      aria-label={runInput?.label ?? 'Insumo'}
                      spellCheck={false}
                    />
                  ) : (
                    <input
                      className={styles.insumo}
                      value={insumo}
                      onChange={(e) => setInsumo(e.target.value)}
                      placeholder={runInput?.placeholder ?? 'Insumo para testar'}
                      aria-label={runInput?.label ?? 'Insumo'}
                    />
                  )}
                  {runSuportado ? (
                    <>
                      <Button
                        variant="secondary"
                        onClick={() => run.mutate(stageId ?? undefined)}
                        disabled={!podeRodar || !stageId}
                        title="Roda do começo até o nó selecionado"
                      >
                        {run.isPending && run.variables === stageId ? 'A rodar…' : 'Rodar até o nó'}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => run.mutate(undefined)}
                        disabled={!podeRodar}
                      >
                        {run.isPending && run.variables === undefined ? 'A rodar…' : 'Rodar teste'}
                      </Button>
                    </>
                  ) : (
                    <span className={styles.semRunner}>
                      Este fluxo não declara runner de teste; o backend não tem o que executar,
                      então não há botão.
                    </span>
                  )}
                </div>
              </header>

              {rascunhos.error && (
                <PainelDeErro
                  endpoint="GET /posts?status=draft"
                  o_que="A lista de matérias em rascunho que alimenta o seletor de insumo"
                  erro={rascunhos.error}
                />
              )}

              {run.isError && (
                <p className={styles.err}>
                  Falha ao rodar: {(run.error as Error)?.message || String(run.error)}
                </p>
              )}
              {run.data?.summary && <p className={styles.meta}>{run.data.summary}</p>}

              <FlowTimeline
                stages={stages}
                selectedId={stageId}
                onSelect={setStageId}
                outputs={outputs}
                runningId={run.isPending ? (run.variables ?? null) : null}
              />

              {stage && (
                <NodePanel
                  stage={stage}
                  value={edits[stage.id] ?? stage.role_system ?? ''}
                  onChange={(v) => setEdits((p) => ({ ...p, [stage.id]: v }))}
                  onRevert={() => setEdits((p) => ({ ...p, [stage.id]: stage.role_system ?? '' }))}
                  onSave={() => save.mutate(stage)}
                  onRestore={() => restore.mutate(stage)}
                  salvando={save.isPending && save.variables?.id === stage.id}
                  restaurando={restore.isPending && restore.variables?.id === stage.id}
                  out={outputs[stage.id]}
                  laudo={
                    laudoInline || edicaoId ? (
                      <div className={styles.laudo}>
                        {/* Caminho do TESTE: dry_run não persiste nada, então o
                            laudo chega inline na resposta do run. */}
                        {laudoInline != null && (
                          <>
                            <span className={styles.laudoTag}>
                              Laudo desta simulação (nada foi gravado)
                            </span>
                            <pre className={styles.laudoPre}>
                              {JSON.stringify(laudoInline, null, 2)}
                            </pre>
                          </>
                        )}
                        {/* Caminho do HISTÓRICO: edição persistida, lida por id. */}
                        {edicaoId && (
                          <>
                            <span className={styles.laudoTag}>
                              Laudo persistido da edição {edicaoId}
                            </span>
                            <PainelDeErro
                              endpoint={`GET /engineering/flows/${flowId}/edicoes/{id}`}
                              o_que="O laudo persistido desta execução"
                              erro={laudoQuery.error}
                            />
                            {laudoQuery.data && (
                              <pre className={styles.laudoPre}>
                                {JSON.stringify(laudoQuery.data, null, 2)}
                              </pre>
                            )}
                          </>
                        )}
                      </div>
                    ) : null
                  }
                />
              )}

              {save.isError && (
                <p className={styles.err}>
                  Falha ao gravar o prompt: {(save.error as Error)?.message || String(save.error)}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {aba === 'parametros' && <WireseoParams />}
      {aba === 'registros' && <WireseoRegistry />}
      {aba === 'fontes' && <WireseoFontes />}
      {aba === 'esteiras' && <WireseoEsteiras />}
    </section>
  );
}
